import { useCallback, useEffect, useRef } from 'react';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { useQueryClient } from 'react-query';
import { MockPersons } from '@frontend/api-person';
import { DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockInterceptorEntry, RequestInterceptorEntry, ResponseInterceptorEntry, useFetch } from '@frontend/fetch';
import { useLastUsedVerticalShallowStore } from '@frontend/location-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { CallIntelligenceV2 } from '../..';
import { useCallIntelDemoShallowStore } from '../../store';
import { CallIntelDemoTriggerSource } from '../../types';
import {
  CallIntelFeedbackMockRouter,
  CallIntelFollowupMockRouter,
  CallIntelInsightPanelMockRouter,
  CallIntelOverviewMockRouter,
  CallIntelRecordsMockRouter,
  CallIntelServiceQualityMockRouter,
  ContactPanelMockRouter,
  CallIntelFiltersMockRouter,
  CallIntelTasksMockRouter,
  CallIntelTssMockRouter,
  CallIntelPmsSyncMockRouter,
  CsvDownloadMockRouter,
} from '../feature-mock-routers';
import { CallIntelDemoInterceptorsContext } from '../feature-mock-routers/types';

const INTERCEPTOR_ID_PREFIX = 'call-intel-demo';

const CACHE_TIME = 1000 * 60 * 60; // 1 hour

const DEFAULT_MOCK_ROUTERS = [
  CallIntelOverviewMockRouter,
  CallIntelRecordsMockRouter,
  ContactPanelMockRouter,
  CallIntelInsightPanelMockRouter,
  CallIntelFiltersMockRouter,
  CallIntelFollowupMockRouter,
  CallIntelTasksMockRouter,
  CallIntelServiceQualityMockRouter,
  CallIntelTssMockRouter,
  CallIntelPmsSyncMockRouter,
  CsvDownloadMockRouter,
  CallIntelFeedbackMockRouter,
];
export interface UseCallIntelDemoInterceptorProps {
  enabled: boolean;
  interceptorRouters?: FeatureMockRouter<any>[];
  demoTriggerSource?: CallIntelDemoTriggerSource;
  interceptorIdPrefix?: string;
  locationIds?: string[];
}

export const useCallIntelDemoInterceptors = ({
  enabled,
  interceptorRouters = DEFAULT_MOCK_ROUTERS,
  demoTriggerSource = 'FULL_MODULE_DEMO',
  interceptorIdPrefix = INTERCEPTOR_ID_PREFIX,
  locationIds = [],
}: UseCallIntelDemoInterceptorProps) => {
  const {
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    removeRequestInterceptor,
    removeResponseInterceptor,
    removeMockInterceptor,
  } = useFetch();

  const { lastUsedVertical: vertical } = useLastUsedVerticalShallowStore('lastUsedVertical');
  const { selectedLocationIds: scopedLocationIds, getScopeName } = useAppScopeStore();

  const { demoDataReadyMap, setIsDemoDataReady } = useCallIntelDemoShallowStore(
    'demoDataReadyMap',
    'setIsDemoDataReady'
  );

  const queryClient = useQueryClient();
  const interceptorsInitializedRef = useRef(false);

  const locationId = locationIds[0] ?? scopedLocationIds[0] ?? '';

  const requestInterceptors = useRef<RequestInterceptorEntry[]>([]);
  const responseInterceptors = useRef<ResponseInterceptorEntry[]>([]);
  const mockInterceptor = useRef<MockInterceptorEntry | null>(null);

  const { data: demoPersonsData, isFetched: isDemoPersonsFetched } = CallIntelligenceV2.Queries.useGetDemoPersonsQuery({
    request: {},
    options: {
      enabled,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
    },
  });

  const { isActive: hasVoiceAiAccess } = useVoiceAIRCustomizationFlag(scopedLocationIds);

  const getMockDataContext = useCallback((): CallIntelDemoInterceptorsContext | null => {
    if (!demoPersonsData || !isDemoPersonsFetched) return null;

    const mockPersonsInstance = new MockPersons({ persons: demoPersonsData.persons ?? [] });
    return {
      vertical: vertical || Vertical.DENTAL,
      locationId,
      mockPersonsInstance,
      interceptorIdPrefix: INTERCEPTOR_ID_PREFIX,
      getScopeName,
      hasVoiceAiAccess,
    };
  }, [demoPersonsData, vertical, locationId, isDemoPersonsFetched, getScopeName, hasVoiceAiAccess]);

  // Invalidate queries for all interceptors
  const invalidateQueries = useCallback(() => {
    return interceptorRouters.forEach((router) => router.invalidateQueries?.(queryClient));
  }, [interceptorRouters, queryClient]);

  const setupInterceptors = useCallback(() => {
    const context = getMockDataContext();
    if (!context) {
      setIsDemoDataReady(demoTriggerSource, false);
      return;
    }
    try {
      requestInterceptors.current = DemoInterceptorUtils.getRequestInterceptors(interceptorRouters, context);
      responseInterceptors.current = DemoInterceptorUtils.getResponseInterceptors(interceptorRouters, context);
      mockInterceptor.current = DemoInterceptorUtils.getMockRouter(interceptorRouters, context, interceptorIdPrefix);
      addMockInterceptor(mockInterceptor.current);
      requestInterceptors.current.forEach(addRequestInterceptor);
      responseInterceptors.current.forEach(addResponseInterceptor);
      invalidateQueries();
      interceptorsInitializedRef.current = true;
      setIsDemoDataReady(demoTriggerSource, true);
    } catch (error) {
      console.error('Error setting up interceptors for call intel', error);
    }
  }, [
    getMockDataContext,
    setIsDemoDataReady,
    demoTriggerSource,
    interceptorRouters,
    interceptorIdPrefix,
    addMockInterceptor,
    addRequestInterceptor,
    addResponseInterceptor,
    invalidateQueries,
  ]);

  const cleanup = useCallback(() => {
    if (interceptorsInitializedRef.current) {
      mockInterceptor.current?.id && removeMockInterceptor(mockInterceptor.current.id);
      requestInterceptors.current?.map(({ id }) => id).forEach(removeRequestInterceptor);
      responseInterceptors.current?.map(({ id }) => id).forEach(removeResponseInterceptor);
      invalidateQueries();
      setIsDemoDataReady(demoTriggerSource, false);
    }
  }, [
    invalidateQueries,
    removeMockInterceptor,
    removeRequestInterceptor,
    removeResponseInterceptor,
    setIsDemoDataReady,
    demoTriggerSource,
  ]);

  useEffect(() => {
    if (enabled) {
      setupInterceptors();
    } else {
      cleanup();
    }
  }, [cleanup, enabled, setupInterceptors]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  const isDemoDataReady = demoDataReadyMap[demoTriggerSource];

  return {
    isDemoDataReady,
    isDemoDataLoading: enabled && !isDemoDataReady,
  };
};
